import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadAnnotations,
  loadSNGAnnotations,
  saveAnnotations,
  deleteAnnotation,
  addAnnotation,
  updateAnnotation,
  cleanUpRegions,
  tagAnnotation,
  clearError,
  reset,
} from ".";
import { NOT_TAGGED } from "../../pages/app/utils/constants";

/**
 * Main hook for annotations functionality
 */
export const useAnnotations = () => {
  const dispatch = useDispatch();
  const annotations = useSelector((state) => state.annotations);

  // Actions
  const handleLoadAnnotations = useCallback(
    (caseId, clipId) => {
      return dispatch(loadAnnotations({ caseId, clipId }));
    },
    [dispatch],
  );

  const handleLoadSNGAnnotations = useCallback(() => {
    return dispatch(loadSNGAnnotations());
  }, [dispatch]);

  const handleSaveAnnotations = useCallback(
    (caseId, clipId, annotationsToSave, untaggedUpdate = false) => {
      return dispatch(
        saveAnnotations({
          caseId,
          clipId,
          annotations: annotationsToSave || annotations.annotations,
          untaggedUpdate,
        }),
      );
    },
    [dispatch, annotations.annotations],
  );

  const handleDeleteAnnotation = useCallback(
    (caseId, clipId, annotationId) => {
      return dispatch(deleteAnnotation({ caseId, clipId, annotationId }));
    },
    [dispatch],
  );

  const handleAddAnnotation = useCallback(
    (annotation) => {
      return dispatch(addAnnotation(annotation));
    },
    [dispatch],
  );

  const handleUpdateAnnotation = useCallback(
    (annotationUpdate) => {
      return dispatch(updateAnnotation(annotationUpdate));
    },
    [dispatch],
  );

  const handleTagAnnotation = useCallback(
    (caseId, clipId, tag) => {
      return dispatch(tagAnnotation({ caseId, clipId, tag }));
    },
    [dispatch],
  );

  const handleClearError = useCallback(() => {
    return dispatch(clearError());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    return dispatch(reset());
  }, [dispatch]);

  // Computed values
  const taggedAnnotations = useMemo(
    () => annotations.annotations.filter((a) => a.tag && a.tag !== NOT_TAGGED),
    [annotations.annotations],
  );

  const untaggedAnnotations = useMemo(
    () => annotations.annotations.filter((a) => !a.tag || a.tag === NOT_TAGGED),
    [annotations.annotations],
  );

  const annotationsByTag = useMemo(() => {
    const grouped = {};
    annotations.annotations.forEach((annotation) => {
      const tag = annotation.tag || "";
      if (!grouped[tag]) {
        grouped[tag] = [];
      }
      grouped[tag].push(annotation);
    });
    return grouped;
  }, [annotations.annotations]);

  return {
    // Core state
    annotations: annotations.annotations,
    tagList: annotations.tagList,
    isLoading: annotations.isLoading,
    isSaving: annotations.isSaving,
    error: annotations.error,
    lastSyncTime: annotations.lastSyncTime,
    tagUpdateNumber: annotations.tagUpdateNumber,

    // Computed values
    taggedAnnotations,
    untaggedAnnotations,
    annotationsByTag,

    // State helpers
    hasAnnotations: annotations.annotations.length > 0,
    hasError: !!annotations.error,
    needsSync:
      annotations.lastSyncTime && Date.now() - annotations.lastSyncTime > 30000, // 30 seconds

    // Actions
    loadAnnotations: handleLoadAnnotations,
    loadSNGAnnotations: handleLoadSNGAnnotations,
    saveAnnotations: handleSaveAnnotations,
    deleteAnnotation: handleDeleteAnnotation,
    addAnnotation: handleAddAnnotation,
    updateAnnotation: handleUpdateAnnotation,
    cleanUpRegions,
    tagAnnotation: handleTagAnnotation,
    clearError: handleClearError,
    reset: handleReset,
  };
};
